import React, { useState, useMemo, useCallback } from 'react';
import { Plus, Truck, Package, ChevronRight, Trash2, ToggleLeft, ToggleRight, Pencil, Search } from 'lucide-react';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { PageHeader } from '../../components/ui/PageHeader';
import { toast } from 'sonner';
import { MarkdownEditor } from '../../components/external-photographers/MarkdownEditor';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Drawer } from '../../components/ui/Drawer';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { BulkActionBar } from '../../components/ui/BulkActionBar';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';
import { useTableSelection } from '../../hooks/useTableSelection';
import { InfoSection, InfoRow } from '../../components/ui/InfoCard';
import { useDebounce } from '../../hooks/useDebounce';
import { formatDate, formatCurrency } from '../../utils/format';
import { AttributeRow, type AttrForm, emptyAttr } from '../Settings/products/AttributeRow';
import {
  useSuppliers, useSupplier, useSupplierProducts,
  useCreateSupplier, useUpdateSupplier, useDeleteSupplier,
  useCreateSupplierProduct, useUpdateSupplierProduct, useToggleSupplierProduct,
  useBulkActivateSuppliers, useBulkDeactivateSuppliers, useBulkDeleteSuppliers,
} from '../../api/useSuppliers';
import type { Supplier, SupplierProduct } from '../../api/types';

type SupplierSortField = 'name' | 'city' | 'products' | 'status';
type SortOrder = 'asc' | 'desc';

function sortSuppliers(list: Supplier[], field: SupplierSortField, order: SortOrder) {
  return [...list].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'name': cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`); break;
      case 'city': cmp = (a.city ?? '').localeCompare(b.city ?? ''); break;
      case 'products': cmp = (a._count?.products ?? 0) - (b._count?.products ?? 0); break;
      case 'status': cmp = Number(a.active) - Number(b.active); break;
    }
    return order === 'asc' ? cmp : -cmp;
  });
}

interface SupplierForm {
  studioName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  address: string;
  vatNumber: string;
  fiscalCode: string;
  notes: string;
  validUntil: string;
  active: boolean;
}

const emptyForm: SupplierForm = { studioName: '', firstName: '', lastName: '', email: '', phone: '', city: '', address: '', vatNumber: '', fiscalCode: '', notes: '', validUntil: '', active: true };

interface ProductForm {
  name: string;
  description: string;
  unitCost: number;
  serviceType: string;
  category: string;
  unit: string;
  attributes: AttrForm[];
}

const emptyProductForm: ProductForm = { name: '', description: '', unitCost: 0, serviceType: '', category: '', unit: '', attributes: [] };

export default function SuppliersPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [showInactive, setShowInactive] = useState(false);
  const [sortBy, setSortBy] = useState<SupplierSortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerTab, setDrawerTab] = useState<'info' | 'products'>('info');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState<SupplierProduct | null>(null);
  const [productForm, setProductForm] = useState(emptyProductForm);

  const params = useMemo(() => {
    const p: Record<string, string | number | boolean> = { active: !showInactive ? true : false };
    return p;
  }, [showInactive]);
  const { data: rawSuppliers = [], isLoading } = useSuppliers(params);
  const sel = useTableSelection(search + showInactive);

  const handleSort = useCallback((field: string) => {
    setSortBy(prev => {
      if (prev === field) { setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); return prev; }
      setSortOrder('asc');
      return field as SupplierSortField;
    });
  }, []);

  const suppliers = useMemo(() => {
    let filtered = rawSuppliers;
    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      filtered = filtered.filter(sp =>
        (sp.studioName ?? '').toLowerCase().includes(s) ||
        sp.firstName.toLowerCase().includes(s) ||
        sp.lastName.toLowerCase().includes(s) ||
        (sp.email ?? '').toLowerCase().includes(s) ||
        (sp.city ?? '').toLowerCase().includes(s)
      );
    }
    return sortSuppliers(filtered, sortBy, sortOrder);
  }, [rawSuppliers, debouncedSearch, sortBy, sortOrder]);

  const { data: selected } = useSupplier(selectedId ?? '');
  const { data: products = [] } = useSupplierProducts(selectedId ?? '');

  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const deleteMutation = useDeleteSupplier();
  const bulkActivate = useBulkActivateSuppliers();
  const bulkDeactivate = useBulkDeactivateSuppliers();
  const bulkDelete = useBulkDeleteSuppliers();
  const createProduct = useCreateSupplierProduct();
  const updateProductMutation = useUpdateSupplierProduct();
  const toggleProductMutation = useToggleSupplierProduct();

  const displayName = (s: Supplier) => s.studioName ?? `${s.firstName} ${s.lastName}`;

  const handleCreate = useCallback(async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) { toast.error('Nome e Cognome sono obbligatori'); return; }
    try {
      await createMutation.mutateAsync({
        ...form, email: form.email.trim() || undefined, phone: form.phone.trim() || undefined,
        validUntil: form.validUntil || null, active: form.active,
      } as any);
      toast.success('Fornitore creato');
      setShowCreateModal(false);
      setForm(emptyForm);
    } catch { /* */ }
  }, [form, createMutation]);

  const handleEdit = useCallback(async () => {
    if (!selected) return;
    try {
      await updateMutation.mutateAsync({
        id: selected.id, data: {
          ...form, email: form.email.trim() || undefined, phone: form.phone.trim() || undefined,
          validUntil: form.validUntil || null, active: form.active,
        } as any,
      });
      toast.success('Fornitore aggiornato');
      setEditMode(false);
    } catch { /* */ }
  }, [selected, form, updateMutation]);

  const openEdit = useCallback(() => {
    if (!selected) return;
    setForm({
      studioName: selected.studioName ?? '', firstName: selected.firstName, lastName: selected.lastName,
      email: selected.email ?? '', phone: selected.phone ?? '', city: selected.city ?? '',
      address: selected.address ?? '', vatNumber: selected.vatNumber ?? '',
      fiscalCode: selected.fiscalCode ?? '', notes: selected.notes ?? '',
      validUntil: selected.validUntil ? selected.validUntil.slice(0, 10) : '',
      active: selected.active,
    });
    setEditMode(true);
  }, [selected]);

  const columns: ColumnDef<Supplier>[] = useMemo(() => [
    { key: 'name', label: 'Nome', sortable: true, render: (s: Supplier) => <span style={{ fontWeight: 500 }}>{displayName(s)}</span> },
    { key: 'city', label: 'Citta', render: (s: Supplier) => <>{s.city ?? ''}</> },
    { key: 'products', label: 'Prodotti', sortable: true, render: (s: Supplier) => <>{s._count?.products ?? 0}</> },
    { key: 'status', label: 'Stato', sortable: true, render: (s: Supplier) => <Badge variant={s.active ? 'success' : 'muted'}>{s.active ? 'Attivo' : 'Inattivo'}</Badge> },
    {
      key: 'actions', label: '', render: (s: Supplier) => (
        <RowActionsMenu actions={[
          { label: 'Visualizza', icon: <ChevronRight size={13} />, onClick: () => { setSelectedId(s.id); setDrawerTab('info'); } },
          { label: 'Modifica', icon: <Pencil size={13} />, onClick: () => { setSelectedId(s.id); openEdit(); } },
          { label: s.active ? 'Disattiva' : 'Attiva', icon: s.active ? <ToggleRight size={13} /> : <ToggleLeft size={13} />,
            onClick: () => updateMutation.mutate({ id: s.id, data: { active: !s.active } }) },
          { label: 'Elimina', icon: <Trash2 size={13} />, onClick: () => setDeleteTargetId(s.id) },
        ]} />
      ),
    },
  ], [updateMutation, openEdit]);

  const activeProducts = products.filter(p => p.isActive);

  const handleProductSave = async () => {
    if (!selected || !productForm.name.trim()) { toast.error('Nome prodotto richiesto'); return; }
    try {
      const payload = {
        ...productForm,
        attributes: productForm.attributes.filter(a => a.key.trim()),
      };
      if (editProduct) {
        await updateProductMutation.mutateAsync({ supplierId: selected.id, productId: editProduct.id, data: payload as any });
        toast.success('Prodotto aggiornato');
      } else {
        await createProduct.mutateAsync({ supplierId: selected.id, data: payload as any });
        toast.success('Prodotto creato');
      }
      setShowProductModal(false);
      setEditProduct(null);
      setProductForm(emptyProductForm);
    } catch { /* */ }
  };

  const productColumns: ColumnDef<SupplierProduct>[] = useMemo(() => [
    { key: 'name', label: 'Nome', render: (p: SupplierProduct) => (
      <div>
        <span style={{ opacity: p.isActive ? 1 : 0.5 }}>{p.name}</span>
        {(p.attributes ?? []).filter(a => a.key).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem', marginTop: '0.2rem' }}>
            {(p.attributes ?? []).filter(a => a.key).map(a => (
              <span key={a.key} style={{ fontSize: '10px', padding: '0.0625rem 0.375rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-interactive)', color: 'var(--color-text-muted)' }}>
                {a.key}{a.defaultValue ? `: ${a.defaultValue}` : ''}{a.unit ? a.unit : ''}
              </span>
            ))}
          </div>
        )}
      </div>
    ) },
    { key: 'category', label: 'Categoria', render: (p: SupplierProduct) => <>{p.category ?? ''}</> },
    { key: 'unitCost', label: 'Costo', render: (p: SupplierProduct) => <>{formatCurrency(p.unitCost)}</> },
    { key: 'unit', label: 'Unita', render: (p: SupplierProduct) => <>{p.unit ?? ''}</> },
    {
      key: 'actions', label: '', render: (p: SupplierProduct) => (
        <RowActionsMenu actions={[
          { label: 'Modifica', icon: <Pencil size={13} />, onClick: () => {
            setEditProduct(p);
            setProductForm({
              name: p.name, description: p.description ?? '', unitCost: p.unitCost,
              serviceType: p.serviceType ?? '', category: p.category ?? '', unit: p.unit ?? '',
              attributes: (p.attributes ?? []).map(a => ({
                key: a.key, attributeType: a.attributeType, defaultValue: a.defaultValue ?? '',
                options: a.options ?? '', unit: a.unit ?? '', isRequired: a.isRequired, sortOrder: a.sortOrder,
              })),
            });
            setShowProductModal(true);
          } },
          { label: p.isActive ? 'Disattiva' : 'Attiva', icon: p.isActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />, onClick: () => toggleProductMutation.mutate({ supplierId: selected!.id, productId: p.id }) },
        ]} />
      ),
    },
  ], [selected, toggleProductMutation]);

  return (
    <div>
      <PageHeader
        title="Fornitori"
        subtitle={`${suppliers.length} fornitori`}
        actions={[
          { label: 'Nuovo Fornitore', icon: <Plus size={16} />, onClick: () => { setForm(emptyForm); setShowCreateModal(true); }, variant: 'primary' },
        ]}
      />

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
        <div style={{ flex: 1, maxWidth: 400 }}>
          <Input placeholder="Cerca fornitori..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Button variant={showInactive ? 'primary' : 'ghost'} size="sm" onClick={() => setShowInactive(!showInactive)}>
          {showInactive ? 'Tutti' : 'Solo attivi'}
        </Button>
      </div>

      {sel.selectedIds.size > 0 && (
        <BulkActionBar
          count={sel.selectedIds.size}
          onClear={sel.clearSelection}
          actions={[
            { label: 'Attiva', icon: <ToggleRight size={13} />, variant: 'secondary' as const, onClick: () => bulkActivate.mutate([...sel.selectedIds]), loading: bulkActivate.isPending },
            { label: 'Disattiva', icon: <ToggleLeft size={13} />, variant: 'secondary' as const, onClick: () => bulkDeactivate.mutate([...sel.selectedIds]), loading: bulkDeactivate.isPending },
            { label: 'Elimina', icon: <Trash2 size={13} />, variant: 'danger' as const, onClick: () => bulkDelete.mutate([...sel.selectedIds]), loading: bulkDelete.isPending },
          ]}
        />
      )}

      {isLoading ? <SkeletonRow /> : suppliers.length === 0 ? (
        <EmptyState icon={<Truck size={48} />} title="Nessun fornitore" description="Aggiungi il primo fornitore per iniziare" />
      ) : (
        <DataTable
          columns={columns}
          data={suppliers}
          gridColumns="2fr 1.5fr 0.8fr 0.8fr 44px"
          rowKey={(s: Supplier) => s.id}
          onRowClick={(s: Supplier) => { setSelectedId(s.id); setDrawerTab('info'); }}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          emptyState={<EmptyState icon={<Truck size={48} />} title="Nessun fornitore" description="Aggiungi il primo fornitore per iniziare" />}
        />
      )}

      <Drawer open={!!selectedId} onClose={() => { setSelectedId(null); setEditMode(false); }} title={selected ? displayName(selected) : ''} width={640}>
        {selected && (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <Button variant={drawerTab === 'info' ? 'primary' : 'ghost'} size="sm" onClick={() => setDrawerTab('info')}>Info</Button>
              <Button variant={drawerTab === 'products' ? 'primary' : 'ghost'} size="sm" onClick={() => setDrawerTab('products')}>
                <Package size={14} style={{ marginRight: 4 }} /> Prodotti ({activeProducts.length})
              </Button>
            </div>

            {drawerTab === 'info' && (
              <>
                <InfoSection title="Dettagli">
                  <InfoRow label="Nome" value={`${selected.firstName} ${selected.lastName}`} />
                  {selected.studioName && <InfoRow label="Studio" value={selected.studioName} />}
                  {selected.email && <InfoRow label="Email" value={selected.email} />}
                  {selected.phone && <InfoRow label="Telefono" value={selected.phone} />}
                  {selected.city && <InfoRow label="Citta" value={selected.city} />}
                  {selected.address && <InfoRow label="Indirizzo" value={selected.address} />}
                  {selected.vatNumber && <InfoRow label="P.IVA" value={selected.vatNumber} />}
                  {selected.fiscalCode && <InfoRow label="Cod. Fiscale" value={selected.fiscalCode} />}
                  {selected.validUntil && <InfoRow label="Validita" value={formatDate(selected.validUntil)} />}
                  <InfoRow label="Stato" value={selected.active ? 'Attivo' : 'Inattivo'} />
                  {selected._count && <InfoRow label="Utilizzi" value={`${selected._count.eventServices ?? 0} servizi`} />}
                </InfoSection>
                {selected.notes && <InfoSection title="Note"><p style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{selected.notes}</p></InfoSection>}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <Button variant="secondary" onClick={openEdit}>Modifica</Button>
                  <Button variant="danger" onClick={() => setDeleteTargetId(selected.id)}>Elimina</Button>
                </div>
              </>
            )}

            {drawerTab === 'products' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                  <Button variant="primary" size="sm" onClick={() => { setEditProduct(null); setProductForm(emptyProductForm); setShowProductModal(true); }}>
                    <Plus size={14} /> Prodotto
                  </Button>
                </div>
                {products.length === 0 ? (
                  <EmptyState icon={<Package size={32} />} title="Nessun prodotto" description="Aggiungi prodotti a questo fornitore" />
                ) : (
                  <DataTable
                    columns={productColumns}
                    data={products}
                    gridColumns="2fr 1fr 0.8fr 0.6fr 44px"
                    rowKey={(p: SupplierProduct) => p.id}
                  />
                )}
              </>
            )}
          </>
        )}
      </Drawer>

      <Modal open={showCreateModal || editMode} onClose={() => { setShowCreateModal(false); setEditMode(false); }} title={editMode ? 'Modifica Fornitore' : 'Nuovo Fornitore'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Nome *" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
            <Input label="Cognome *" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Studio" value={form.studioName} onChange={e => setForm({ ...form, studioName: e.target.value })} />
            <Input label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Telefono" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            <Input label="Citta" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
          </div>
          <Input label="Indirizzo" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="P.IVA" value={form.vatNumber} onChange={e => setForm({ ...form, vatNumber: e.target.value })} />
            <Input label="Codice Fiscale" value={form.fiscalCode} onChange={e => setForm({ ...form, fiscalCode: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', alignItems: 'center' }}>
            <Input label="Valido fino" type="date" value={form.validUntil} onChange={e => setForm({ ...form, validUntil: e.target.value })} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 0 }}>Attivo</label>
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })}
                style={{ width: 18, height: 18, accentColor: 'var(--color-primary)', cursor: 'pointer' }} />
            </div>
          </div>
          <MarkdownEditor label="Note" value={form.notes} onChange={v => setForm({ ...form, notes: v })} rows={3} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
            <Button variant="ghost" onClick={() => { setShowCreateModal(false); setEditMode(false); }}>Annulla</Button>
            <Button variant="primary" onClick={editMode ? handleEdit : handleCreate} loading={createMutation.isPending || updateMutation.isPending}>
              {editMode ? 'Salva' : 'Crea'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={showProductModal} onClose={() => setShowProductModal(false)} title={editProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'} width={560}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Nome *" value={productForm.name} onChange={e => setProductForm({ ...productForm, name: e.target.value })} />
            <Input label="Categoria" value={productForm.category} onChange={e => setProductForm({ ...productForm, category: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Tipo servizio</label>
              <select value={productForm.serviceType} onChange={e => setProductForm({ ...productForm, serviceType: e.target.value })}
                style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%' }}>
                <option value="">— nessuno —</option>
                <option value="ALBUM">Album</option>
                <option value="STAMPA_FINE_ART">Stampa Fine Art</option>
                <option value="STAMPA_RISTORANTE">Stampa Ristorante</option>
                <option value="MINI_ALBUM_GENITORI">Mini Album Genitori</option>
                <option value="POSTER">Poster</option>
                <option value="USB_PERSONALIZZATA">USB Personalizzata</option>
                <option value="VIDEO_MATRIMONIO">Video Matrimonio</option>
                <option value="TRAILER">Trailer</option>
                <option value="REEL">Reel</option>
                <option value="SDE">SDE</option>
                <option value="REPORTAGE_FOTO">Reportage Foto</option>
                <option value="SHOOTING_PRE">Shooting Pre</option>
                <option value="SHOOTING_POST">Shooting Post</option>
                <option value="DRONE">Drone</option>
                <option value="SECONDO_FOTOGRAFO">Secondo Fotografo</option>
                <option value="VIDEOMAKER">Videomaker</option>
                <option value="ALTRO">Altro</option>
              </select>
            </div>
            <Input label="Descrizione" value={productForm.description} onChange={e => setProductForm({ ...productForm, description: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Costo unitario" type="number" value={productForm.unitCost === 0 ? '' : String(productForm.unitCost)} onChange={e => setProductForm({ ...productForm, unitCost: Number(e.target.value) || 0 })} />
            <Input label="Unita" value={productForm.unit} onChange={e => setProductForm({ ...productForm, unit: e.target.value })} placeholder="es. a pagina" />
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Attributi custom ({productForm.attributes.length})
              </span>
              <Button variant="ghost" size="sm" onClick={() => setProductForm({ ...productForm, attributes: [...productForm.attributes, emptyAttr(productForm.attributes.length)] })}>
                <Plus size={12} /> Attributo
              </Button>
            </div>
            {productForm.attributes.length === 0 ? (
              <div style={{ padding: '1rem', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 'var(--radius-md)', textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                Nessun attributo. Aggiungi attributi per definire varianti (es. dimensioni, numero pagine, tipo copertina).
              </div>
            ) : (
              productForm.attributes.map((attr, idx) => (
                <AttributeRow
                  key={`${idx}-${attr.key}`}
                  attr={attr}
                  index={idx}
                  total={productForm.attributes.length}
                  onChange={(a) => setProductForm({ ...productForm, attributes: productForm.attributes.map((x, i) => i === idx ? a : x) })}
                  onRemove={() => setProductForm({ ...productForm, attributes: productForm.attributes.filter((_, i) => i !== idx) })}
                  onMove={(dir) => {
                    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
                    if (newIdx < 0 || newIdx >= productForm.attributes.length) return;
                    const arr = [...productForm.attributes];
                    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
                    setProductForm({ ...productForm, attributes: arr });
                  }}
                />
              ))
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
            <Button variant="ghost" onClick={() => setShowProductModal(false)}>Annulla</Button>
            <Button variant="primary" onClick={handleProductSave} loading={createProduct.isPending || updateProductMutation.isPending}>
              {editProduct ? 'Salva' : 'Crea'}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => { deleteTargetId && deleteMutation.mutate(deleteTargetId); setDeleteTargetId(null); if (selected?.id === deleteTargetId) setSelectedId(null); }}
        title="Elimina fornitore"
        description="Il fornitore verra disattivato (soft delete). I prodotti e le associazioni esistenti non verranno rimossi."
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
